import { Svg, G as SVGG } from "@svgdotjs/svg.js";
import { GridPoints, IPoint, Poly } from "../grids/_base.js";
import { APRenderRep } from "../schemas/schema.js";
import { IRendererOptionsIn, RendererBase } from "./_base.js";
import { centroid, projectPoint, rotatePoint, usePieceAt } from "../common/plotting.js";
import { cairoCatalan, cairoCollinear, cobweb, conhex, conicalHex, fracturedFlat, hexOfCir, hexOfHex, hexOfTri, hexOfTriF, hexSlanted, moon, onyx, bricka, pentagonal, bentTri, star, pyramidHex, rectOfHex, rectOfTri, snubSquare, snubSquareCells, sowingRound, squares, squaresDiamonds, vertex, wheel } from "../boards/index.js";

/**
 * The `stacking-offset` renderer creates stacks of pieces by offsetting them slightly to give a 3D look.
 *
 */
export class StackingOffsetRenderer extends RendererBase {

    public static readonly rendererName: string = "stacking-offset";
    constructor() {
        super();
    }

    public render(json: APRenderRep, draw: Svg, options: IRendererOptionsIn): void {
        this.jsonPrechecks(json);
        if (this.json === undefined) {
            throw new Error("JSON prechecks fatally failed.");
        }
        this.optionsPrecheck(options);
        this.rootSvg = draw;

        if (this.json.board === null) {
            throw new Error("This renderer requires that `board` be defined.");
        }

        // BOARD
        // Delegate to style-specific renderer
        let gridPoints: GridPoints;
        let pcGrid: GridPoints|undefined;
        let polys: Poly[][]|undefined;
        let boardFill: Poly|undefined;
        if (! ("style" in this.json.board)) {
            throw new Error(`This 'board' schema cannot be handled by the '${ StackingOffsetRenderer.rendererName }' renderer.`);
        }

        // Load all the pieces in the legend (have to do this early so the glyphs are available for marking the board)
        this.loadLegend();

        switch (this.json.board.style) {
            case "squares-checkered":
            case "squares-beveled":
            case "squares":
                ({ grid: gridPoints, polys, boardFill } = squares(this));
                break;
            case "squares-diamonds":
            case "squares-diamonds-checkered":
                ({ grid: gridPoints, polys, boardFill } = squaresDiamonds(this));
                break;
            case "vertex":
            case "vertex-cross":
            case "vertex-fanorona":
                ({ grid: gridPoints, boardFill } = vertex(this));
                break;
            case "snubsquare":
                ({ grid: gridPoints, boardFill } = snubSquare(this));
                break;
            case "onyx":
                ({ grid: gridPoints, boardFill } = onyx(this));
                break;
            case "bricka":
                ({ grid: gridPoints, polys, boardFill } = bricka(this));
                break;
            case "snubsquare-cells":
                ({ grid: gridPoints, polys, boardFill } = snubSquareCells(this));
                break;
            case "pentagonal":
            case "pentagonal-bluestone":
                ({ grid: gridPoints, boardFill } = pentagonal(this));
                break;
            case "bent-tri":
                ({ grid: gridPoints, boardFill } = bentTri(this));
                break;
            case "star":
                ({ grid: gridPoints, boardFill } = star(this));
                break;
            case "hex-of-hex":
                ({ grid: gridPoints, polys, boardFill } = hexOfHex(this));
                break;
            case "hex-of-tri":
                ({ grid: gridPoints, boardFill } = hexOfTri(this));
                break;
            case "hex-of-tri-f":
                ({ grid: gridPoints, polys, boardFill } = hexOfTriF(this));
                break;
            case "hex-of-cir":
                ({ grid: gridPoints, polys, boardFill } = hexOfCir(this));
                break;
            case "rect-of-tri":
                ({ grid: gridPoints, boardFill } = rectOfTri(this));
                break;
            // case "rect-of-tri-f":
            //     [gridPoints, polys] = this.rectOfTriF();
            //     break;
            case "hex-slanted":
                ({ grid: gridPoints, polys, boardFill } = hexSlanted(this));
                break;
            case "hex-odd-p":
            case "hex-even-p":
            case "hex-odd-f":
            case "hex-even-f":
                ({ grid: gridPoints, polys, boardFill } = rectOfHex(this));
                break;
            case "circular-cobweb":
                ({ grid: gridPoints, polys, boardFill } = cobweb(this));
                break;
            case "circular-wheel":
            case "circular-wheel-spaces":
            case "circular-wheel-vertices":
            case "circular-wheel-spaces-checkered":
                ({ grid: gridPoints, polys, boardFill } = wheel(this));
                break;
            case "conhex-cells":
                ({ grid: gridPoints, polys, boardFill } = conhex(this));
                break;
            case "cairo-collinear":
                ({ grid: gridPoints, polys, boardFill } = cairoCollinear(this));
                break;
            case "cairo-catalan":
                ({ grid: gridPoints, polys, boardFill } = cairoCatalan(this));
                break;
            // Adding support for conical-hex* and pyramid-hex purely for the designer
            case "conical-hex":
            case "conical-hex-narrow":
                ({ grid: gridPoints, polys, boardFill } = conicalHex(this));
                break;
            case "pyramid-hex":
                ({ grid: gridPoints, polys, boardFill } = pyramidHex(this));
                break;
            case "circular-moon":
                this.cellsize = 15;
                ({ grid: gridPoints, polys, boardFill } = moon(this));
                break;
            case "fractured-flat":
                this.cellsize = 40;
                ({ grid: gridPoints, polys, boardFill } = fracturedFlat(this));
                break;
            case "sowing-round":
                ({ grid: gridPoints, boardFill } = sowingRound(this));
                break;
            default:
                throw new Error(`The requested board style (${ this.json.board.style }) is not supported by the '${ StackingOffsetRenderer.rendererName }' renderer.`);
        }

        // PIECES
        const board = this.rootSvg.findOne("#board") as SVGG;
        const group = board.group().id("pieces");
        if (this.json.pieces !== null) {
            // Generate pieces array
            let pieces: string[][][] = [];

            if (typeof this.json.pieces === "string") {
                // Does it contain commas
                if (this.json.pieces.indexOf(",") >= 0) {
                    for (const row of this.json.pieces.split("\n")) {
                        let node: string[][] = [];
                        if (row === "_") {
                            node = new Array(this.json.board.width).fill([]) as string[][];
                        } else {
                            const cells = row.split(",");
                            for (const cell of cells) {
                                if (cell === "") {
                                    node.push([]);
                                } else {
                                    node.push([...cell]);
                                }
                            }
                        }
                        pieces.push(node);
                    }
                } else {
                    throw new Error("This renderer requires that you use the comma-delimited or array format of the `pieces` property.");
                }
            } else if ( (this.json.pieces instanceof Array) && (this.json.pieces[0] instanceof Array) && (this.json.pieces[0][0] instanceof Array) ) {
                pieces = this.json.pieces as string[][][];
            } else {
                throw new Error("Unrecognized `pieces` property.");
            }

            /**
             * Place the pieces according to the grid.
             * This is done in a two-pass process so that stacks are rendered,
             * as well as we can, from the "top" to the "bottom" so lower stacks
             * overlap higher ones. So first generate a matrix of keys and coordinates,
             * then rotate that matrix as necessary and render from top to bottom.
            */
            const rotation = this.getRotation();
            let offsetPercent = 0.13;
            if ( ("stackOffset" in this.json.board) && (this.json.board.stackOffset !== undefined) ) {
                offsetPercent = this.json.board.stackOffset;
            }
            const offset = this.cellsize * offsetPercent;
            const ctr = centroid(gridPoints.flat().flat())!;
            type Entry = {
                key: string;
                x: number;
                y: number;
                rotx: number;
                roty: number;
                row: number;
                col: number;
                idx: number;
            }
            const pcs: Entry[][] = [];
            for (let row = 0; row < pieces.length; row++) {
                for (let col = 0; col < pieces[row].length; col++) {
                    const entry: Entry[] = []
                    for (let i = 0; i < pieces[row][col].length; i++) {
                        const key = pieces[row][col][i];
                        if ( (key !== null) && (key !== "-") ) {
                            let point: IPoint;
                            if (pcGrid !== undefined) {
                                point = pcGrid[row][col];
                            } else {
                                point = gridPoints[row][col];
                            }
                            const dist = offset * i;
                            const [offsetX, offsetY] = projectPoint(point.x, point.y, dist, rotation * -1);
                            const rot = rotatePoint({x: offsetX, y: offsetY}, rotation, ctr);
                            entry.push({key, x: offsetX, y: offsetY, row, col, idx: i, rotx: rot.x, roty: rot.y});
                        }
                    }
                    if (entry.length > 0) {
                        pcs.push(entry);
                    }
                }
            }
            // sort the cells top to bottom, left to right, based on first entry
            pcs.sort((a,b) => {
                const a0 = a[0];
                const b0 = b[0];
                if (a0.roty === b0.roty) {
                    return a0.rotx - b0.rotx;
                } else {
                    return a0.roty - b0.roty;
                }
            });
            for (const cell of pcs) {
                for (const entry of cell) {
                    const { key, x, y, row, col, idx } = entry;
                    const piece = this.rootSvg.findOne("#" + key) as Svg;
                    if ( (piece === null) || (piece === undefined) ) {
                        throw new Error(`Could not find the requested piece (${key}). Each piece in the \`pieces\` property *must* exist in the \`legend\`.`);
                    }
                    let sheetCellSize = piece.viewbox().h;
                    if ( (sheetCellSize === null) || (sheetCellSize === undefined) ) {
                        sheetCellSize = piece.attr("data-cellsize") as number;
                        if ( (sheetCellSize === null) || (sheetCellSize === undefined) ) {
                            throw new Error(`The glyph you requested (${key}) does not contain the necessary information for scaling. Please use a different sheet or contact the administrator.`);
                        }
                    }
                    const use = usePieceAt({svg: group, piece, cellsize: this.cellsize, x, y, scalingFactor: 0.85});
                    if ( (this.options.boardClick !== undefined) && (! this.json.options?.includes("no-piece-click")) ) {
                        use.click((e: Event) => {
                            this.options.boardClick!(row, col, idx.toString());
                            e.stopPropagation?.();
                        });
                    } else {
                        use.attr({"pointer-events": "none"});
                    }
                }
            }
        }

        // annotations
        if (this.options.showAnnotations) {
            this.annotateBoard(gridPoints, polys);
        }

        // if there's a board backfill, it needs to be done before rotation
        const backfilled = this.backFill(boardFill, true);

        const box = this.rotateBoard();

        const layoutBox = this.placeTrackAreas(box);

        // `pieces` area, if present
        this.piecesArea(layoutBox);

        // button bar
        this.placeButtonBar(layoutBox);

        // key
        this.placeKey(layoutBox);

        // compassRose
        this.placeCompass(layoutBox);

        if (!backfilled) {
            this.backFill(boardFill);
        }
    }
}
